import { Injectable } from '@angular/core';
import { PROJECT_TYPES } from '@purespectrum1/ui';
import { ProjectResponse } from 'src/app/shared/services/buyer-api/survey.interface';

type Formatter = {
  getIdentifier: (r: ProjectResponse) => string;
  label: string;
};

@Injectable({
  providedIn: 'root',
})
export class ErrorMessageFormatterService {
  private readonly _errorFormatters: Record<number, Formatter> = {
    [PROJECT_TYPES.MCQB]: {
      getIdentifier: (r: ProjectResponse) => {
        const survey = r.survey;
        if (survey && 'countryCode' in survey && 'languageCode' in survey) {
          return `${survey.countryCode} - ${survey.languageCode}`;
        }
        return 'unknown locale';
      },
      label: 'locales',
    },

    [PROJECT_TYPES.TRACKER]: {
      getIdentifier: (r: ProjectResponse) => {
        const survey = r.survey;
        if (survey && 'wave_id' in survey) {
          return `${survey.wave_id}`;
        }
        return 'unknown wave';
      },
      label: 'waves',
    },
  };

  private _isPatchError(r: ProjectResponse): boolean {
    return r.message?.ps_api_response_message?.includes('PATCH') ?? false;
  }

  private _formatNonPatchError(r: ProjectResponse): string {
    const formatter = this._errorFormatters[r.projectType!];
    if (!formatter) {
      return '';
    }

    const identifier = formatter.getIdentifier(r);

    return `${
      r.message?.ps_api_response_message
    } for this ${formatter.label.slice(0, -1)}: ${identifier}`;
  }

  private _formatPatchErrorMessage(patchErrors: ProjectResponse[]): string {
    const formatter = this._errorFormatters[patchErrors[0]?.projectType!];
    if (!formatter) {
      return '';
    }

    const identifiers = patchErrors
      .map((r) => formatter.getIdentifier(r))
      .join(', ');

    return `We got an error while changing the status for these ${formatter.label}: ${identifiers}. Check the survey and try again.`;
  }

  public constructErrorMessage(errorResponses: ProjectResponse[]): {
    message: string;
    details?: string[];
  } {
    const nonPatchErrors = errorResponses.filter((r) => !this._isPatchError(r));

    const patchErrors = errorResponses.filter((r) => this._isPatchError(r));

    const details = nonPatchErrors
      .map((r) => this._formatNonPatchError(r))
      .filter(Boolean);

    if (patchErrors.length) {
      details.push(this._formatPatchErrorMessage(patchErrors));
    }

    return {
      message: 'Multiple errors occurred',
      details,
    };
  }
}
